import { expressionFunctions } from '@lmt/expressions/src/expressions/helpers/expression-functions';
import {
  ConditionType,
  MergeFieldQueryType,
  QueryFactory,
} from '@lmt/shared-types';
import { CreateMergeFieldDto } from '@lmt/shared-types';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import {
  usePrefixLinkOptions,
  useOnClone,
} from '@lmt/unicorn-shared/app/components/documents/components/helpers';
import {
  getDataBlockFunctionDeclarations,
  getFunctionDeclarations,
} from '@lmt/unicorn-shared/app/components/documents/components/intellisense-helper';
import { MonacoExpressionBuilder } from '@lmt/unicorn-shared/app/common/Monaco/MonacoExpressionBuilder';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { emptyArray } from '@lmt/unicorn-shared/app/helpers/common';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { DropDownInput } from '../../common/ui-kit/inputs/dropdown/DropDownInput';
import styles from './QueryBuilderForm.module.scss';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import clsx from 'clsx';
import * as yup from 'yup';
import i18next from 'i18next';
import { ICreateMergeFieldDto } from '@lmt/unicorn-shared/test/nock-client';
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckBox } from '../../common/ui-kit/CheckBox';
import {
  MergeFieldInterpretationsTable,
  rangeConditions,
  simpleConditions,
} from './MergeFieldInterpretationsTable';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';

const mergeFieldyupSchema: yup.Schema<Partial<ICreateMergeFieldDto>> =
  yup.object({
    prefix: yup
      .string()
      .trim()
      .max(3, () =>
        i18next.t('translation:errors.max_length', {
          maxLength: 3,
        }),
      )
      .required(() => i18next.t('translation:errors.required')),
    code: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(0)
      .lessThan(1000000, () =>
        i18next.t('translation:documents.query_builder.errors.code_less_than'),
      ),
    name: yup
      .string()
      .trim()
      .required(() => i18next.t('translation:errors.required')),
    expression: yup
      .string()
      .trim()
      .required(() => i18next.t('translation:errors.required')),
    isInterpretationsActive: yup.bool(),
    defaultInterpretationEntry: yup.string().trim().nullable(),
    interpretations: yup
      .array()
      .of(
        yup.object().shape({
          position: yup.number().required(),
          condition: yup.mixed() as any,
          value: yup
            .number()
            .nullable()
            .when('condition', {
              is: (condition: ConditionType) =>
                simpleConditions.includes(condition),
              then: (schema) => schema.required(),
            }),
          minValue: yup
            .number()
            .nullable()
            .when('condition', {
              is: (condition: ConditionType) =>
                rangeConditions.includes(condition),
              then: (schema) => schema.required(),
            }),
          maxValue: yup
            .number()
            .nullable()
            .when('condition', {
              is: (condition: ConditionType) =>
                rangeConditions.includes(condition),
              then: (schema) => schema.required(),
            }),
          entry: yup.string().nullable(),
        }),
      )
      .nullable(),
  });

type Form = CreateMergeFieldDto;
const nameof = nameofFactory<Form>();

type Props = {
  mergeFieldId?: number;
  formId: number;
};

export const QueryBuilderForm: React.FC<Props> = React.memo(
  function QueryBuilderForm(props) {
    const modals = useModal();
    const i18n = useScopedTranslation('documents.query_builder');
    const claims = useClaims();
    const globalForms = useGlobalForms();
    const queryClient = useQueryClient();
    const dataBlockTypeQuery = QueryFactory.DataBlockTypeQuery.useGetQuery();
    const mergeFieldQuery = QueryFactory.MergeFieldQuery.useGetQueriesQuery();

    const editedMergeField = QueryFactory.MergeFieldQuery.useGetSingleQuery(
      props.mergeFieldId!,
      {
        enabled: !!props.mergeFieldId,
      },
    );
    const isReadOnly =
      editedMergeField.data?.isDefault === true ||
      !claims.permissions.mergeFieldManagement;

    const form = useAdvancedForm<Form>(
      async (data: Form) => {
        try {
          if (props.mergeFieldId) {
            await QueryFactory.MergeFieldClient.patch(props.mergeFieldId, data);
            queryClient.setQueryData(
              QueryFactory.MergeFieldQuery.getSingleQueryKey(
                props.mergeFieldId,
              ),
              {
                ...editedMergeField,
                ...data,
              },
            );
          } else {
            await QueryFactory.MergeFieldClient.create(data);
          }
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.MergeFieldQuery.getQueryKey(),
          });
          globalForms.closeForm(props.formId);
        } catch (e) {
          await modals.showError({
            text: convertToErrorString(e, { excludeFieldName: true }),
          });
        }
      },
      props.mergeFieldId
        ? editedMergeField.data!
        : useMemo(
            () => ({
              expression: '',
              prefix: 'U',
              name: '',
              code: undefined,
              isInterpretationsActive: false,
              defaultInterpretationEntry: '',
              interpretations: [],
            }),
            [],
          ),
      {
        resolver: yupResolver(mergeFieldyupSchema),
        shouldFocusError: true,
        criteriaMode: 'all',
      },
    );

    // we can add prefix here in the future which is used for making available code input field
    const isCodeEditable = false;

    useEffect(
      function resetCode() {
        if (!isCodeEditable) {
          form.setValue('code', undefined);
        }
      },
      [isCodeEditable],
    );

    const copyToNameLinkProps = useMemo(
      () => ({
        title: i18n.t('copy_to_field_name'),
        onClick: () => {
          form.setValue(nameof('name'), form.getValues(nameof('expression')), {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        },
      }),
      [],
    );

    const onExpressionChange = useCallback((value: string) => {
      form.setValue(nameof('expression'), value, {
        shouldValidate: true,
      });
    }, []);
    const [loadingTitle, setLoadingTitle] = useState<string | undefined>(
      undefined,
    );
    const handleDelete = async () => {
      try {
        if (
          await modals.showDeletionConfirmation({
            text: i18n.t('delete_field_confirmation_text'),
          })
        ) {
          setLoadingTitle(i18n.t('delete_loading'));

          await QueryFactory.MergeFieldClient.remove(props.mergeFieldId!);
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.MergeFieldQuery.getQueryKey(),
          });
          globalForms.closeForm(props.formId);
        }
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        setLoadingTitle(undefined);
      }
    };

    const prefixes = QueryFactory.MergeFieldPrefixQuery.useGetQuery({
      isUserPrefix: true,
    });

    const intellisenseContent = useMemo(() => {
      if (!dataBlockTypeQuery.data || !mergeFieldQuery.data) return '';
      const dataBlockTypes = dataBlockTypeQuery.data ?? [];
      const documentQueries = mergeFieldQuery.data ?? [];

      return [
        ...expressionFunctions.map((x) => `declare function ${x.signature};`),
        getFunctionDeclarations(
          ['patient'],
          'PatientType',
          documentQueries,
          MergeFieldQueryType.PatientQuery,
        ),
        getFunctionDeclarations(
          ['visit', 'previousVisit', 'nextVisit'],
          'VisitType',
          documentQueries,
          MergeFieldQueryType.VisitQuery,
        ),
        getFunctionDeclarations(
          ['firstVisitInCase', 'lastVisitInCase'],
          'VisitType',
          documentQueries,
          MergeFieldQueryType.VisitQuery,
        ),
        getFunctionDeclarations(['allVisits', 'selectedVisits'], 'VisitType[]'),
        getFunctionDeclarations(
          ['Case'],
          'CaseType',
          documentQueries,
          MergeFieldQueryType.CaseQuery,
        ),
        getFunctionDeclarations(
          ['file'],
          'FileType',
          documentQueries,
          MergeFieldQueryType.FileQuery,
        ),
        getFunctionDeclarations(
          ['hl7'],
          'HL7Type',
          documentQueries,
          MergeFieldQueryType.HL7Settings,
        ),
        getFunctionDeclarations(
          ['gdt'],
          'GDTType',
          documentQueries,
          MergeFieldQueryType.GDTSettings,
        ),
        getDataBlockFunctionDeclarations(
          [
            'block',
            'lastBlockInVisit',
            'firstBlockInVisit',
            'lastBlockInCase',
            'firstBlockInCase',
            'previousBlock',
          ],
          documentQueries,
          dataBlockTypes,
        ),
        getDataBlockFunctionDeclarations(
          ['nthBlockInVisit'],
          documentQueries,
          dataBlockTypes,
          'n: number',
          true,
        ),
        getDataBlockFunctionDeclarations(
          ['allBlocksInVisit', 'allBlocksInCase'],
          documentQueries,
          dataBlockTypes,
          undefined,
          true,
          true,
        ),
      ].join('\n');
    }, [dataBlockTypeQuery.data, mergeFieldQuery.data]);

    const [showInterpretations] = form.watch(['isInterpretationsActive']);
    const [showErrors, setShowErrors] = useState<boolean>(false);

    const onShowInterpretationsChange = useCallback(
      async (checked: boolean) => {
        if (!checked) {
          const result = await modals.showConfirm({
            text: i18n.t('delete_interpretations_confirmation_text'),
          });
          if (result) {
            form.setValue('defaultInterpretationEntry', null);
            form.setValue('interpretations', null);
            form.setValue('isInterpretationsActive', false);
          } else {
            form.setValue('isInterpretationsActive', true);
          }
        } else {
          form.setValue('isInterpretationsActive', true);
        }
      },
      [],
    );

    const onClone = useOnClone();
    const handleClone = async () => {
      await onClone(editedMergeField.data!, {
        onSuccess: () => globalForms.closeForm(props.formId),
        onLoading: () => setLoadingTitle(i18n.t('copy_loading')),
        onFinish: () => setLoadingTitle(undefined),
      });
    };

    return (
      <Loading
        loading={
          mergeFieldQuery.isLoading ||
          editedMergeField.isLoading ||
          !!loadingTitle ||
          form.formState.isSubmitting
        }
        title={loadingTitle}
      >
        <div
          className={clsx('globalFormContentRoot', styles.popupStyles)}
          data-test-id="queryBuilderFormPopup"
        >
          <form onSubmit={form.handleSubmitDefault}>
            <div className={'globalFormContentBody'}>
              {props.mergeFieldId && (
                <div className={styles.code}>
                  {i18n.t('code_value', { value: editedMergeField.data?.code })}
                </div>
              )}

              <Field
                required={!props.mergeFieldId}
                title={i18n.t('prefix_field_title')}
                className={globalFormStyles.field}
                linkProps={
                  props.mergeFieldId
                    ? undefined
                    : usePrefixLinkOptions((newPrefix) => {
                        form.setValue('prefix', newPrefix, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      })
                }
                hint={i18n.t('prefix_hint')}
              >
                <Controller
                  control={form.control}
                  name={'prefix'}
                  render={({ field: { onChange, value } }) => (
                    <DropDownInput
                      required={true}
                      variant={'formInput'}
                      options={prefixes.data?.prefixes ?? emptyArray()}
                      value={value}
                      onValueChanged={(value) => {
                        onChange(value);
                        if (form.formState.errors.prefix) {
                          form.clearErrors('prefix');
                        }
                      }}
                      errorText={form.formState.errors.prefix?.message}
                      disabled={!!props.mergeFieldId}
                      testId="prefix"
                    />
                  )}
                />
              </Field>

              {isCodeEditable && (
                <Field
                  title={i18n.t('code_field_title')}
                  className={globalFormStyles.field}
                  hint={i18n.t('code_hint')}
                >
                  <Input
                    type={'number'}
                    {...form.register(nameof('code'))}
                    readOnly={isReadOnly}
                    errorText={form.formState.errors.code?.message}
                  />
                </Field>
              )}

              <Field
                required={!props.mergeFieldId}
                title={i18n.t('name_field_title')}
                className={globalFormStyles.field}
              >
                <Input
                  {...form.register(nameof('name'))}
                  defaultValue={''}
                  readOnly={isReadOnly}
                  errorText={form.formState.errors.name?.message}
                />
              </Field>

              <Field
                title={i18n.t('select_field_title')}
                required={!props.mergeFieldId}
                className={globalFormStyles.field}
                linkProps={isReadOnly ? undefined : copyToNameLinkProps}
              >
                <MonacoExpressionBuilder
                  defaultExpressionValue={editedMergeField.data?.expression}
                  error={!!form.formState.errors.expression}
                  errorText={form.formState.errors.expression?.message}
                  onExpressionChanged={onExpressionChange}
                  intellisenseContent={intellisenseContent}
                  isReadOnly={isReadOnly}
                />
              </Field>

              {!editedMergeField.data?.isDefault && (
                <CheckBox
                  className={styles.checkbox}
                  {...form.register('isInterpretationsActive')}
                  onChange={(e) =>
                    onShowInterpretationsChange(e.currentTarget.checked)
                  }
                  disabled={isReadOnly}
                  title={i18n.t('show_interpretation')}
                />
              )}
              {showInterpretations && (
                <>
                  <MergeFieldInterpretationsTable
                    form={form}
                    showErrors={showErrors}
                    isReadOnly={isReadOnly}
                  />

                  <Field
                    title={i18n.t('placeholder')}
                    className={clsx(globalFormStyles.field, styles.lastField)}
                  >
                    <Input
                      {...form.register(nameof('defaultInterpretationEntry'))}
                      readOnly={isReadOnly}
                      errorText={
                        form.formState.errors.defaultInterpretationEntry
                          ?.message
                      }
                    />
                  </Field>
                </>
              )}
            </div>
            {claims.permissions.mergeFieldManagement && (
              <div
                className={clsx(
                  'globalFormContentFooter',
                  styles['controles-container'],
                )}
              >
                {isReadOnly ? (
                  <Button
                    onClick={handleClone}
                    className={globalFormStyles.submit}
                    title={i18n.t('copy_button_title')}
                  />
                ) : (
                  <>
                    {props.mergeFieldId && (
                      <Button
                        onClick={handleDelete}
                        color={ButtonColor.Danger}
                        className={globalFormStyles.submit}
                        title={i18n.t('delete_field_button')}
                      />
                    )}
                    {props.mergeFieldId && (
                      <Button
                        onClick={handleClone}
                        color={ButtonColor.Primary}
                        className={globalFormStyles.submit}
                        title={i18n.t('copy_button_title')}
                      />
                    )}
                    <SubmitButton
                      submitting={form.formState.isSubmitting}
                      className={globalFormStyles.submit}
                      title={i18n.t('save_button_title')}
                      onClick={() => setShowErrors(true)}
                    />
                  </>
                )}
              </div>
            )}
          </form>
        </div>
      </Loading>
    );
  },
);
